import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Scissors, Trash2, Copy } from 'lucide-react';
import { useEditorStore } from '../store/editorStore';
import { usePlayheadStore } from '../store/playheadStore';
import { useLoopStore } from '../store/loopStore';
import { EFFECT_CATALOG, type EffectType } from '../engine/effectsManager';
import { useEffectClipDialogStore } from '../store/effectClipDialogStore';

export interface ContextMenuState {
  x: number;
  y: number;
  clipId: string;
  isEffectClip?: boolean;
  hasSelection?: boolean;
}

interface Props {
  menu: ContextMenuState;
  onClose: () => void;
}

// Group effects by category
const effectCategories = Object.entries(EFFECT_CATALOG).reduce<Record<string, { key: string; label: string }[]>>(
  (acc, [key, val]) => {
    (acc[val.category] ??= []).push({ key, label: val.label });
    return acc;
  },
  {},
);

export function TimelineContextMenu({ menu, onClose }: Props) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [showEffectMenu, setShowEffectMenu] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const currentTimeMs = usePlayheadStore((s) => s.currentTimeMs);

  // Check if playhead is inside the selected clip (for split availability)
  const clip = useEditorStore((s) => {
    if (menu.isEffectClip || menu.hasSelection) return null;
    for (const track of s.project.tracks) {
      const c = track.clips.find((c) => c.id === menu.clipId);
      if (c) return c;
    }
    return null;
  });

  const canSplit =
    clip != null &&
    currentTimeMs > clip.startMs + 100 &&
    currentTimeMs < clip.startMs + clip.durationMs - 100;

  // Clamp menu position to viewport after render
  const [pos, setPos] = useState({ x: menu.x, y: menu.y });
  useEffect(() => {
    setPos({ x: menu.x, y: menu.y });
    // Wait for next frame so the menu has rendered and we can measure it
    requestAnimationFrame(() => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const pad = 8;
      let x = menu.x;
      let y = menu.y;
      if (x + rect.width > vw - pad) x = vw - rect.width - pad;
      if (y + rect.height > vh - pad) y = vh - rect.height - pad;
      if (x < pad) x = pad;
      if (y < pad) y = pad;
      setPos({ x, y });
    });
  }, [menu.x, menu.y]);

  // Close on click outside or Escape
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('mousedown', handleClick);
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('mousedown', handleClick);
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  const handleSplit = () => {
    if (!canSplit) return;
    useEditorStore.getState().splitClip(menu.clipId, currentTimeMs);
    onClose();
  };

  const handleDeleteRequest = () => {
    if (menu.isEffectClip) {
      // Check if this is the last clip on its track
      const state = useEditorStore.getState();
      for (const track of state.project.tracks) {
        if (track.type !== 'effect' || !track.effectClips) continue;
        const clip = track.effectClips.find((ec) => ec.id === menu.clipId);
        if (clip && track.effectClips.length === 1) {
          setConfirmDelete(true);
          return;
        }
      }
    }
    doDelete();
  };

  const doDelete = () => {
    if (menu.isEffectClip) {
      useEditorStore.getState().removeEffectClip(menu.clipId);
    } else {
      useEditorStore.getState().removeClip(menu.clipId);
    }
    useEditorStore.getState().selectClip(null);
    setConfirmDelete(false);
    onClose();
  };

  const handleDuplicate = () => {
    const state = useEditorStore.getState();
    for (const track of state.project.tracks) {
      const clip = track.clips.find((c) => c.id === menu.clipId);
      if (clip) {
        const newStart = clip.startMs + clip.durationMs;
        state.addClip(track.id, {
          assetId: clip.assetId,
          startMs: newStart,
          durationMs: clip.durationMs,
          sourceOffsetMs: clip.sourceOffsetMs,
        });
        break;
      }
    }
    onClose();
  };

  const handleAddEffect = (effectType: string) => {
    const loop = useLoopStore.getState();
    if (loop.selectionStart === null || loop.selectionEnd === null) return;

    const selStart = Math.min(loop.selectionStart, loop.selectionEnd);
    const selEnd = Math.max(loop.selectionStart, loop.selectionEnd);

    const catalog = EFFECT_CATALOG[effectType as EffectType];
    useEffectClipDialogStore.getState().openForAdd(
      effectType, selStart, selEnd, { ...catalog.defaults },
    );
    onClose();
  };

  // Delete confirmation overlay (for effect clips that are last on their track)
  if (confirmDelete) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="w-72 bg-[#1C1C1E] border border-[#3A3A3C] rounded-xl shadow-2xl shadow-black/60 overflow-hidden">
          <div className="px-4 py-4 text-center">
            <p className="text-sm text-[#E5E5EA] font-medium mb-1">
              {t('editor.deleteEffectTitle', 'Delete Effect?')}
            </p>
            <p className="text-xs text-[#8E8E93]">
              {t('editor.deleteEffectDesc', 'This will remove the effect and its track from the timeline.')}
            </p>
          </div>
          <div className="flex border-t border-[#3A3A3C]">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 text-sm text-[#0A84FF] hover:bg-[#2A2A2E] transition-colors border-r border-[#3A3A3C]"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={doDelete}
              className="flex-1 py-2.5 text-sm font-medium text-red-400 hover:bg-red-400/10 transition-colors"
            >
              {t('editor.delete', 'Delete')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Effect clip context menu
  if (menu.isEffectClip) {
    return (
      <div
        ref={ref}
        className="fixed z-50 min-w-[180px] py-1 rounded-lg bg-[#1C1C1E] border border-[#3A3A3C] shadow-xl shadow-black/50"
        style={{ left: pos.x, top: pos.y }}
      >
        <button
          onClick={handleDeleteRequest}
          className="w-full flex items-center gap-2.5 px-3 py-1.5 text-sm text-start transition-colors hover:bg-[#2C2C2E] cursor-pointer text-red-400"
        >
          <Trash2 size={14} className="shrink-0 opacity-70" />
          <span className="flex-1">{t('editor.delete', 'Delete')}</span>
          <span className="text-xs opacity-40 font-mono">{'\u232B'}</span>
        </button>
      </div>
    );
  }

  // Selection context menu — Add Effect
  if (menu.hasSelection) {
    return (
      <div
        ref={ref}
        className="fixed z-50 min-w-[200px] py-1 rounded-lg bg-[#1C1C1E] border border-[#3A3A3C] shadow-xl shadow-black/50"
        style={{ left: pos.x, top: pos.y }}
      >
        {!showEffectMenu ? (
          <button
            onClick={() => setShowEffectMenu(true)}
            className="w-full flex items-center gap-2.5 px-3 py-1.5 text-sm text-start transition-colors hover:bg-[#2C2C2E] cursor-pointer text-[#E5E5EA]"
          >
            <span className="text-[10px] font-bold text-[#A855F7]">FX</span>
            <span className="flex-1">{t('editor.addEffect', 'Add Effect')}</span>
            <span className="text-xs opacity-40">{'>'}</span>
          </button>
        ) : (
          <>
            {Object.entries(effectCategories).map(([category, effects]) => (
              <div key={category}>
                <div className="px-3 py-1 text-[10px] text-[#636366] font-medium uppercase tracking-wider">
                  {category}
                </div>
                {effects.map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => handleAddEffect(key)}
                    className="w-full flex items-center gap-2.5 px-3 py-1.5 text-sm text-start transition-colors hover:bg-[#2C2C2E] cursor-pointer text-[#E5E5EA]"
                  >
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            ))}
          </>
        )}
      </div>
    );
  }

  // Regular clip context menu
  const items = [
    {
      label: t('editor.splitAtPlayhead', 'Split at playhead'),
      shortcut: '\u2318B',
      icon: Scissors,
      action: handleSplit,
      disabled: !canSplit,
    },
    {
      label: t('editor.duplicate', 'Duplicate'),
      shortcut: '',
      icon: Copy,
      action: handleDuplicate,
      disabled: false,
    },
    { type: 'separator' as const },
    {
      label: t('editor.delete', 'Delete'),
      shortcut: '\u232B',
      icon: Trash2,
      action: doDelete,
      disabled: false,
      danger: true,
    },
  ];

  return (
    <div
      ref={ref}
      className="fixed z-50 min-w-[180px] py-1 rounded-lg bg-[#1C1C1E] border border-[#3A3A3C] shadow-xl shadow-black/50"
      style={{ left: pos.x, top: pos.y }}
    >
      {items.map((item, i) => {
        if ('type' in item && item.type === 'separator') {
          return <div key={i} className="my-1 h-px bg-[#3A3A3C]" />;
        }

        const { label, shortcut, icon: Icon, action, disabled, danger } = item as Exclude<typeof item, { type: 'separator' }>;

        return (
          <button
            key={i}
            onClick={action}
            disabled={disabled}
            className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-sm text-start transition-colors
              ${disabled ? 'opacity-30 cursor-not-allowed' : 'hover:bg-[#2C2C2E] cursor-pointer'}
              ${danger && !disabled ? 'text-red-400' : 'text-[#E5E5EA]'}
            `}
          >
            <Icon size={14} className="shrink-0 opacity-70" />
            <span className="flex-1">{label}</span>
            {shortcut && (
              <span className="text-xs opacity-40 font-mono">{shortcut}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
